import { TrendingUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import Background from "../components/background.tsx";
import Header from "../components/header.tsx";
import Sidebar from "../components/sidebar.tsx";
import ChartCard from "../components/statistics/chart-card.tsx";
import CustomBarChart from "../components/statistics/custom-barchart.tsx";
import CustomLineChart from "../components/statistics/custom-linechart.tsx";
import CustomPieChart from "../components/statistics/custom-piechart.tsx";
import TechnoArtLogo from "../components/technoArtLogo.tsx";

// Define interfaces for our data types
interface StatCard {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down";
}

interface ChartDataPoint {
  name: string;
  value: number;
  color: string;
}

interface TimeSeriesDataPoint {
  date: string;
  detections: number;
}

interface HourlyDataPoint {
  hour: string;
  detections: number;
}

interface ConfidenceDataPoint {
  range: string;
  count: number;
}

interface StatisticsData {
  statCards: StatCard[];
  detectionTrendData: TimeSeriesDataPoint[];
  vehicleDistributionData: ChartDataPoint[];
  hourlyActivityData: HourlyDataPoint[];
  plateTypeDistributionData: ChartDataPoint[];
  blacklistDistributionData: ChartDataPoint[];
  confidenceDistributionData: ConfidenceDataPoint[];
  regionDistributionData: ChartDataPoint[];
}

const StatisticsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statisticsData, setStatisticsData] = useState<StatisticsData>({
    statCards: [
      {
        title: "Total Detection",
        value: "0",
        change: "0% from last month",
        trend: "up",
      },
      {
        title: "Vehicle Detected",
        value: "0",
        change: "0% of total detections",
        trend: "up",
      },
      {
        title: "Plate Detected",
        value: "0",
        change: "0% of total detections",
        trend: "up",
      },
      {
        title: "Average Confidence",
        value: "0%",
        change: "0% from last month",
        trend: "up",
      },
    ],
    detectionTrendData: [],
    vehicleDistributionData: [],
    hourlyActivityData: [],
    plateTypeDistributionData: [],
    blacklistDistributionData: [],
    confidenceDistributionData: [],
    regionDistributionData: [],
  });

  // Move fetchStatisticsData outside useEffect but still inside component
  const fetchStatisticsData = async () => {
    try {
      setLoading(true);
      console.log("Fetching statistics data...");

      // Create base URL and headers
      const baseUrl = "https://galaxy-alpr-be.onrender.com/api/statistics";
      const headers = {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };

      // Prepare all the fetch requests
      const dashboardPromise = fetch(`${baseUrl}/dashboard`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      const plateTypesPromise = fetch(`${baseUrl}/plate-types`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      const regionsPromise = fetch(`${baseUrl}/regions`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      const vehicleDistPromise = fetch(`${baseUrl}/vehicle-distribution`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      const detectionTrendPromise = fetch(`${baseUrl}/detection-trend`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      const hourlyActivityPromise = fetch(`${baseUrl}/hourly-activity`, { headers })
        .then(response => response.ok ? response.json() : { error: response.statusText });

      // Execute all requests in parallel
      const [dashboardData, plateTypesData, regionsData,
        vehicleDistData, detectionTrendData, hourlyActivityData] = await Promise.all([
          dashboardPromise, plateTypesPromise, regionsPromise,
          vehicleDistPromise, detectionTrendPromise, hourlyActivityPromise
        ]);

      console.log("All data received successfully");

      // Combine all data - start with dashboard and override with specific endpoints
      const combinedData = {
        ...dashboardData,
        plateTypeDistributionData:
          (plateTypesData.plateTypeDistributionData && plateTypesData.plateTypeDistributionData.length > 0) ?
            plateTypesData.plateTypeDistributionData : dashboardData.plateTypeDistributionData || [],

        regionDistributionData:
          (regionsData.regionDistributionData && regionsData.regionDistributionData.length > 0) ?
            regionsData.regionDistributionData : dashboardData.regionDistributionData || [],

        vehicleDistributionData:
          (vehicleDistData.vehicleDistributionData && vehicleDistData.vehicleDistributionData.length > 0) ?
            vehicleDistData.vehicleDistributionData : dashboardData.vehicleDistributionData || [],

        detectionTrendData:
          (detectionTrendData.detectionTrendData && detectionTrendData.detectionTrendData.length > 0) ?
            detectionTrendData.detectionTrendData : dashboardData.detectionTrendData || [],

        hourlyActivityData:
          (hourlyActivityData.hourlyActivityData && hourlyActivityData.hourlyActivityData.length > 0) ?
            hourlyActivityData.hourlyActivityData : dashboardData.hourlyActivityData || [],
      };

      // Validate and provide defaults for any missing data
      if (!combinedData.statCards || !Array.isArray(combinedData.statCards)) {
        console.error("Missing statCards data");
        combinedData.statCards = [
          {
            title: "Total Detection",
            value: "0",
            change: "No data available",
            trend: "up",
          },
          {
            title: "Vehicle Detected",
            value: "0",
            change: "No data available",
            trend: "up",
          },
          {
            title: "Plate Detected",
            value: "0",
            change: "No data available",
            trend: "up",
          },
          {
            title: "Average Confidence",
            value: "0%",
            change: "No data available",
            trend: "up",
          },
        ];
      }

      // Check for missing data sections and add placeholders
      const dataSections = [
        { key: 'detectionTrendData', name: 'Detection Trend' },
        { key: 'hourlyActivityData', name: 'Hourly Activity' },
        { key: 'vehicleDistributionData', name: 'Vehicle Distribution' },
        { key: 'plateTypeDistributionData', name: 'Plate Type Distribution' },
        { key: 'blacklistDistributionData', name: 'Blacklist Distribution' },
        { key: 'regionDistributionData', name: 'Region Distribution' }
      ];

      const missingSections = dataSections.filter(section =>
        !combinedData[section.key] || !Array.isArray(combinedData[section.key]) || combinedData[section.key].length === 0
      );

      if (missingSections.length > 0) {
        console.warn(`Some data sections are missing: ${missingSections.map(s => s.name).join(', ')}`);
        missingSections.forEach(section => {
          if (section.key.includes('Distribution')) {
            combinedData[section.key] = [{ name: `No ${section.name} Data Available`, value: 1, color: "#CBD5E1" }];
          } else if (section.key.includes('Trend')) {
            combinedData[section.key] = [{ date: "No Data", detections: 0 }];
          } else if (section.key.includes('Activity')) {
            combinedData[section.key] = [{ hour: "No Data", detections: 0 }];
          }
        });
      }

      // Update state with combined data
      setStatisticsData(combinedData);
      console.log("Statistics data updated successfully");
      setError(null);

    } catch (err) {
      console.error("Error fetching statistics data:", err);

      // Set appropriate error message
      if (err.message.includes("Backend server is not running")) {
        setError("Cannot connect to the backend server. Please check if the server is running.");
      } else {
        setError(`Failed to load statistics data: ${err.message}`);
      }

      // Use fallback data
      setStatisticsData({
        statCards: [
          {
            title: "Total Detection",
            value: "0",
            change: "Data unavailable",
            trend: "up",
          },
          {
            title: "Vehicle Detected",
            value: "0",
            change: "Data unavailable",
            trend: "up",
          },
          {
            title: "Plate Detected",
            value: "0",
            change: "Data unavailable",
            trend: "up",
          },
          {
            title: "Average Confidence",
            value: "0%",
            change: "Data unavailable",
            trend: "up",
          },
        ],
        detectionTrendData: [{ "date": "No Data", "detections": 0 }],
        vehicleDistributionData: [{ "name": "No Data", "value": 1, "color": "#CBD5E1" }],
        hourlyActivityData: [{ "hour": "No Data", "detections": 0 }],
        plateTypeDistributionData: [{ "name": "No Data", "value": 1, "color": "#CBD5E1" }],
        blacklistDistributionData: [{ "name": "No Data", "value": 1, "color": "#CBD5E1" }],
        confidenceDistributionData: [{ "range": "No Data", "count": 0 }],
        regionDistributionData: [{ "name": "No Data", "value": 1, "color": "#CBD5E1" }],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Call the fetchStatisticsData function
    fetchStatisticsData();

    // Auto-retry once if there's a connection error
    const retryTimeout = setTimeout(() => {
      if (error && error.includes("Cannot connect")) {
        console.log("Retrying connection to backend...");
        fetchStatisticsData();
      }
    }, 5000); // Wait 5 seconds before retry

    return () => clearTimeout(retryTimeout);
  }, [error]); // Add error as dependency to monitor changes

  // Helper function to ensure chart data is never empty
  const ensureChartData = (data: ChartDataPoint[], chartName: string): ChartDataPoint[] => {
    // Filter out invalid data points
    const filteredData = data.filter(item =>
      item &&
      item.name &&
      item.value > 0 &&
      item.name !== ' ()' // Remove empty region entries
    );

    // If filtered data is empty, return a placeholder
    if (!filteredData || filteredData.length === 0) {
      return [{
        name: `No ${chartName} Data Available`,
        value: 1,
        color: "#CBD5E1"
      }];
    }

    return filteredData;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background />

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <div className="p-8">
            <div className="max-w-8xl mx-auto space-y-6">
              {/* Loading State */}
              {loading && (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              )}

              {/* Error State - only show as warning if we have an actual error */}
              {error && (
                <div
                  className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4"
                  role="alert"
                >
                  <strong className="font-bold">Warning: </strong>
                  <span className="block sm:inline">{error}</span>
                  {error.includes("Cannot connect") && (
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setError(null);
                          setLoading(true);
                          setTimeout(() => fetchStatisticsData(), 100);
                        }}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-xs"
                      >
                        Retry Connection
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setError(null)}
                    className="absolute top-0 right-0 p-2 text-yellow-500 hover:text-yellow-700"
                  >
                    <span className="text-xl">&times;</span>
                  </button>
                </div>
              )}

              {/* Debug information - hidden in production */}
              <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded text-xs" style={{ display: 'none' }}>
                <p>API Status: {error ? "Error" : "OK"}</p>
                <p>Data Status: {statisticsData.statCards.length > 0 ? "Populated" : "Empty"}</p>
                <details>
                  <summary>Raw Data</summary>
                  <pre>{JSON.stringify(statisticsData, null, 2)}</pre>
                </details>
              </div>

              {/* Statistics Cards - show regardless of error state as long as we're not loading */}
              {!loading && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {statisticsData.statCards.map((card, index) => (
                      <div
                        key={index}
                        className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-600">
                            {card.title}
                          </h3>
                          <TrendingUp
                            className={`w-4 h-4 ${card.trend === "up"
                              ? "text-green-500"
                              : "text-red-500"
                              }`}
                          />
                        </div>
                        <div className="mb-2">
                          <span className="text-3xl font-bold text-gray-800">
                            {card.value}
                          </span>
                        </div>
                        <div
                          className={`text-sm ${card.trend === "up" ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {card.change}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Charts Section */}
                  {/* Row 1: Detection Trend & Hourly Activity */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Detection Trend Chart */}
                    <ChartCard
                      title="Detection Trend"
                      subtitle="Daily detection count over time"
                    >
                      <CustomLineChart
                        data={statisticsData.detectionTrendData}
                        dataKey="detections"
                        xKey="date"
                        lineColor="#3B82F6"
                      />
                    </ChartCard>
                    {/* Hourly Activity Bar Chart */}
                    <ChartCard
                      title="Hourly Activity"
                      subtitle="Detections by hour of day"
                    >
                      <CustomBarChart
                        data={statisticsData.hourlyActivityData}
                        dataKey="detections"
                        xKey="hour"
                        barColor="#3B82F6"
                      />
                    </ChartCard>
                  </div>

                  {/* Row 2: Vehicle Distribution & Plate Type Distribution */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Vehicle Distribution Chart */}
                    <ChartCard
                      title="Vehicle Distribution"
                      subtitle="Breakdown by vehicle type"
                    >
                      <div className="h-[28rem] flex items-center justify-center relative">
                        <CustomPieChart data={ensureChartData(
                          statisticsData.vehicleDistributionData,
                          "Vehicle Distribution"
                        )} />
                      </div>
                    </ChartCard>
                    {/* Plate Type Distribution Pie Chart */}
                    <ChartCard
                      title="Plate Type Distribution"
                      subtitle="Breakdown by plate type"
                    >
                      <div className="h-[28rem] flex items-center justify-center relative">
                        <CustomPieChart data={ensureChartData(
                          statisticsData.plateTypeDistributionData,
                          "Plate Type"
                        )} />
                      </div>
                    </ChartCard>
                  </div>

                  {/* Row 3: Blacklist Distribution & Region Distribution */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Blacklist Distribution Pie Chart */}
                    <ChartCard
                      title="Blacklist/Whitelist Distribution"
                      subtitle="Detections by blacklist status"
                    >
                      <div className="h-[28rem] flex items-center justify-center relative">
                        <CustomPieChart data={ensureChartData(
                          statisticsData.blacklistDistributionData,
                          "Blacklist/Whitelist"
                        )} />
                      </div>
                    </ChartCard>
                    {/* Region Distribution Pie Chart */}
                    <ChartCard
                      title="Region Distribution"
                      subtitle="Detections by region"
                    >
                      <div className="h-[28rem] flex items-center justify-center relative">
                        <CustomPieChart data={ensureChartData(
                          statisticsData.regionDistributionData,
                          "Region"
                        )} />
                      </div>
                    </ChartCard>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>
      <TechnoArtLogo />
    </div>
  );
};

export default StatisticsPage;
